/**
 * AI Contract Extraction Service
 *
 * Handles contract document upload and AI-powered data extraction
 * for paid workspace users.
 *
 * ## Architecture
 *
 * The service uses a multi-stage extraction pipeline:
 *
 * 1. **File Upload**: Document is uploaded to Firebase Storage
 * 2. **Stage 1 - Classification**: AI identifies contract type, country, region (~5s)
 * 3. **Stage 2 - Extraction**: AI extracts fields with confidence scores (~20s)
 * 4. **Summary Step**: User reviews and fills missing fields
 * 5. **Form Prefill**: Merged data populates the contract form
 *
 * ## Methods
 *
 * - `extractContractEnhanced()` - Multi-stage extraction pipeline
 * - `extractContractWithSummary()` - Returns data formatted for summary UI
 * - `canUseAIImport()` - Check if workspace has feature access
 * - `canImportContract()` - Check usage limits
 *
 * ## Related Files
 *
 * - Backend: `backend/functions/src/aiContractExtraction.ts`
 * - Types: `src/app/shared/models/contracts/extraction-result.model.ts`
 * - UI: `src/app/pages/apps/contracts/add-contract/extraction-summary/`
 * - Docs: `docs/AI_CONTRACT_IMPORT_SYSTEM.md`
 */

import { Injectable, Injector, inject } from '@angular/core';
import { Observable, from, throwError, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  limit,
  orderBy,
  query
} from '@angular/fire/firestore';
import { PropertyType } from '@shared/definitions/property';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { isWorkspaceCompActive } from '@shared/utils/workspace-comp';
import { toObservable } from '@angular/core/rxjs-interop';
import { errorMessage } from '@shared/utils/error-utils';

/**
 * Narrow view of the workspace fields the AI gate reads. Only `billing`
 * and (as a backwards-compat fallback) `subscription.status` are needed.
 * The full models are owned by billing / workspace services.
 */
interface WorkspaceBillingView {
  entitlement?: 'free' | 'paid';
  hasPaymentMethod?: boolean;
}
interface WorkspaceSubscriptionStatusView {
  status?: string;
  trialEndsAt?: { toMillis?: () => number; seconds?: number } | Date | number | null;
}
import {
  EnhancedExtractionResponse,
  ExtractionSummaryData,
  SupportedCountryCode,
  ExtractionJobDoc
} from '@shared/definitions/contracts/extraction-result.model';
import {
  transformToSummaryData
} from '@shared/definitions/contracts/extraction-result.model';

/**
 * Usage information for AI contract imports
 */
export interface AIImportUsageInfo {
  canImport: boolean;
  used: number;
  limit: number;
  isPaid: boolean;
  /**
   * True when the backend runs on the dev project / emulator, where the monthly
   * cap is lifted. Never true in production — the UI uses it to label the
   * environment, never to unlock anything on its own.
   */
  devBypass?: boolean;
  /**
   * True when the caller is Proporio staff (system role `superadmin`) AND the
   * backend runs outside production, where the monthly cap is lifted for them.
   * Never true in production — like {@link devBypass} the UI uses it to label
   * the state, never to unlock anything on its own.
   */
  superAdminBypass?: boolean;
  /**
   * True when the reported allowance is the lifetime "one import offered"
   * grant rather than the paid monthly pool — i.e. the workspace is NOT paid but
   * may still import once. `used`/`limit` then read `0/1` or `1/1`.
   *
   * Callers must read {@link canImport} to decide, never `isPaid`: a free
   * workspace holding its grant reports `isPaid: false, canImport: true`.
   */
  freeTrial?: boolean;
}

/**
 * Enhanced extraction request
 */
interface EnhancedExtractionRequest {
  fileUrl: string;
  fileName: string;
  fileType: string;
  propertyId: string;
  propertyType: PropertyType;
  workspaceId: string;
  countryHint?: SupportedCountryCode;
  quickMode?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AiContractExtractionService {
  private readonly injector = inject(Injector);

  constructor(
    private functions: Functions,
    private storage: Storage,
    private firestore: Firestore,
    private workspaceStore: WorkspaceStore
  ) {}

  /**
   * Check if user can import contracts for a property
   */
  canImportContract(workspaceId: string, propertyId: string): Observable<AIImportUsageInfo> {
    // `propertyId` is accepted for call-site compatibility only — the backend
    // pool is per workspace and the callable ignores it.
    void propertyId;
    return this.getWorkspaceUsage(workspaceId);
  }

  /**
   * Get current usage for a property
   */
  getUsageForProperty(workspaceId: string, propertyId: string): Observable<AIImportUsageInfo> {
    return this.canImportContract(workspaceId, propertyId);
  }

  /**
   * Read the workspace's monthly AI-import usage without consuming a credit.
   *
   * The allowance is a single per-workspace pool, so no property is needed —
   * this is what lets the add-contract screen show the quota before any
   * property has been picked.
   */
  getWorkspaceUsage(workspaceId: string): Observable<AIImportUsageInfo> {
    const callable = httpsCallable<
      { workspaceId: string },
      AIImportUsageInfo
    >(this.functions, 'getAIContractImportUsage');

    return from(callable({ workspaceId })).pipe(
      map(result => result.data),
      catchError(error => {
        console.error('Error checking import usage:', error);
        return of({
          canImport: false,
          used: 0,
          limit: 0,
          isPaid: false,
          devBypass: false,
          superAdminBypass: false
        });
      })
    );
  }

  // ============================================================================
  // ENHANCED MULTI-STAGE EXTRACTION (NEW)
  // ============================================================================

  /**
   * Extract contract data using the enhanced multi-stage pipeline
   *
   * Stage 1: Classification - Identify contract type, country, region
   * Stage 2: Extraction - Extract fields based on classification
   *
   * Returns structured data with per-field confidence scores
   */
  extractContractEnhanced(
    file: File,
    propertyId: string,
    propertyType: PropertyType,
    countryHint?: SupportedCountryCode,
    quickMode: boolean = false
  ): Observable<EnhancedExtractionResponse> {
    // Validate file
    const validation = this.validateFile(file);
    if (!validation.valid) {
      return throwError(() => new Error(validation.error || 'Invalid file'));
    }

    // Get workspace ID
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      return throwError(() => new Error('No workspace selected'));
    }

    // Step 1: Upload file to storage
    return this.uploadFileToStorage(file, workspaceId, propertyId).pipe(
      switchMap(fileUrl => {
        // Step 2: Call enhanced extraction Cloud Function
        return this.callEnhancedExtractionFunction(
          fileUrl,
          file.name,
          file.type,
          propertyId,
          propertyType,
          workspaceId,
          countryHint,
          quickMode
        );
      }),
      catchError(error => {
        console.error('Enhanced contract extraction error:', error);
        return throwError(() => this.createUserFriendlyError(error));
      })
    );
  }

  /**
   * Extract contract and return summary data for UI
   */
  extractContractWithSummary(
    file: File,
    propertyId: string,
    propertyType: PropertyType,
    countryHint?: SupportedCountryCode
  ): Observable<{ response: EnhancedExtractionResponse; summaryData: ExtractionSummaryData }> {
    return this.extractContractEnhanced(file, propertyId, propertyType, countryHint).pipe(
      map(response => ({
        response,
        summaryData: transformToSummaryData(response)
      }))
    );
  }

  // ============================================================================
  // ASYNC EXTRACTION JOB (non-blocking) — Phase C
  // ============================================================================

  /**
   * Upload the document, then queue an async extraction job. Resolves with the
   * jobId immediately — the heavy extraction runs in a background trigger and
   * the caller subscribes to the job doc via `watchExtractionJob`. This is the
   * non-blocking replacement for `extractContractWithSummary`.
   */
  startExtractionJob(
    file: File,
    propertyId: string,
    propertyType: PropertyType,
    countryHint?: SupportedCountryCode,
    quickMode = false
  ): Observable<{ jobId: string }> {
    const validation = this.validateFile(file);
    if (!validation.valid) {
      return throwError(() => new Error(validation.error || 'Invalid file'));
    }
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      return throwError(() => new Error('No workspace selected'));
    }

    return this.uploadFileToStorage(file, workspaceId, propertyId).pipe(
      switchMap(fileUrl => {
        const callable = httpsCallable<
          EnhancedExtractionRequest,
          { jobId: string; used: number; limit: number }
        >(this.functions, 'startContractExtractionJob');
        const request: EnhancedExtractionRequest = {
          fileUrl,
          fileName: file.name,
          fileType: file.type,
          propertyId,
          propertyType,
          workspaceId,
          countryHint,
          quickMode
        };
        return from(callable(request)).pipe(map(result => ({ jobId: result.data.jobId })));
      }),
      catchError(error => {
        console.error('startExtractionJob error:', error);
        return throwError(() => this.createUserFriendlyError(error));
      })
    );
  }

  /**
   * Re-run an extraction on the document already in Storage — free, whether the
   * previous job FAILED or COMPLETED with a poor result (#362).
   *
   * No re-upload and no new credit: the file is still there, and the server
   * decides from the previous job's status which ledger (if any) to touch. A
   * re-run of a completed job is bounded — `freeRerunsRemaining` says how many
   * are left, and an exhausted budget rejects with `resource-exhausted`.
   *
   * Resolves with the id of the freshly queued job, which the caller watches
   * like any other.
   */
  retryExtractionJob(
    workspaceId: string,
    jobId: string
  ): Observable<{ jobId: string; freeRerunsRemaining: number }> {
    const callable = httpsCallable<
      { workspaceId: string; jobId: string },
      { jobId: string; freeRerunsRemaining: number }
    >(this.functions, 'retryContractExtractionJob');

    return from(callable({ workspaceId, jobId })).pipe(
      map(result => result.data),
      catchError(error => {
        console.error('retryExtractionJob error:', error);
        return throwError(() => this.createUserFriendlyError(error));
      })
    );
  }

  /**
   * Stop an extraction in progress — "je me suis trompé de fichier" (#383).
   *
   * Requests a stop; it does not confirm one happened. The job is a Firestore
   * trigger, not a streamed call this can abort — `watchExtractionJob` is what
   * shows the caller `status` actually turning to `cancelled`, at the
   * pipeline's next checkpoint (seconds away, not instant). `requested: false`
   * means the job had already finished by the time this landed — not an
   * error, the caller just missed the window.
   */
  cancelExtractionJob(workspaceId: string, jobId: string): Observable<{ requested: boolean }> {
    const callable = httpsCallable<
      { workspaceId: string; jobId: string },
      { requested: boolean }
    >(this.functions, 'cancelContractExtractionJob');

    return from(callable({ workspaceId, jobId })).pipe(
      map(result => result.data),
      catchError(error => {
        console.error('cancelExtractionJob error:', error);
        return throwError(() => this.createUserFriendlyError(error));
      })
    );
  }

  /**
   * Realtime stream of an extraction job document. Emits on every status
   * transition (queued → processing → completed/failed). Emits `undefined`
   * until the doc exists / is readable.
   */
  watchExtractionJob(workspaceId: string, jobId: string): Observable<ExtractionJobDoc | undefined> {
    const ref = doc(this.firestore, `workspaces/${workspaceId}/extractionJobs/${jobId}`);
    return docData(ref) as Observable<ExtractionJobDoc | undefined>;
  }

  /**
   * Realtime stream of the workspace's most recent extraction jobs.
   *
   * Drives the "unfinished imports" banner on the contracts list: an extraction
   * that the user never carried through to a saved contract is otherwise only
   * reachable via the completion notification or the session-scoped toolbar
   * indicator, both of which are lost on a hard reload.
   *
   * Capped at 20 — the banner is a recovery affordance, not an audit log.
   */
  listExtractionJobs(workspaceId: string, max = 20): Observable<ExtractionJobDoc[]> {
    const jobs = query(
      collection(this.firestore, `workspaces/${workspaceId}/extractionJobs`),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    return collectionData(jobs, { idField: 'jobId' }) as Observable<ExtractionJobDoc[]>;
  }

  /**
   * Call the enhanced extraction Cloud Function
   */
  private callEnhancedExtractionFunction(
    fileUrl: string,
    fileName: string,
    fileType: string,
    propertyId: string,
    propertyType: PropertyType,
    workspaceId: string,
    countryHint?: SupportedCountryCode,
    quickMode?: boolean
  ): Observable<EnhancedExtractionResponse> {
    const callable = httpsCallable<
      EnhancedExtractionRequest,
      EnhancedExtractionResponse
    >(this.functions, 'extractContractDataEnhanced', {
      timeout: 540_000
    });

    const request: EnhancedExtractionRequest = {
      fileUrl,
      fileName,
      fileType,
      propertyId,
      propertyType,
      workspaceId,
      countryHint,
      quickMode
    };

    return from(callable(request)).pipe(
      map(result => {
        return result.data;
      })
    );
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Validate file before upload
   */
  private validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size must be less than 10MB'
      };
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword' // .doc
    ];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Unsupported file type. Please upload PDF, Word, or image files.'
      };
    }

    return { valid: true };
  }

  /**
   * Upload file to Firebase Storage
   */
  private uploadFileToStorage(
    file: File,
    workspaceId: string,
    propertyId: string
  ): Observable<string> {
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `ai-contract-imports/${workspaceId}/${propertyId}/${timestamp}_${sanitizedFileName}`;

    const storageRef = ref(this.storage, storagePath);

    return from(uploadBytes(storageRef, file)).pipe(
      switchMap(uploadResult => from(getDownloadURL(uploadResult.ref))),
      catchError(error => {
        console.error('File upload error:', error);
        return throwError(() => new Error('Failed to upload file. Please try again.'));
      })
    );
  }

  /**
   * Create user-friendly error message
   */
  private createUserFriendlyError(error: unknown): Error {
    let friendlyMessage = 'Failed to extract contract data. ';

    const code = (error as { code?: string } | null)?.code;
    const rawMessage = errorMessage(error);

    if (code === 'functions/unauthenticated') {
      friendlyMessage += 'Please sign in and try again.';
    } else if (code === 'functions/permission-denied') {
      friendlyMessage += rawMessage || 'This feature requires a paid subscription.';
    } else if (code === 'functions/resource-exhausted') {
      friendlyMessage += rawMessage || 'Monthly import limit reached.';
    } else if (code === 'functions/deadline-exceeded') {
      friendlyMessage += 'Processing took too long. Please try with a smaller or clearer document.';
    } else if (rawMessage) {
      friendlyMessage += rawMessage;
    } else {
      friendlyMessage += 'Please try again or contact support.';
    }

    return new Error(friendlyMessage);
  }

  // ============================================================================
  // WORKSPACE CHECKS
  // ============================================================================

  /**
   * Whether the workspace can use AI contract import.
   *
   * Reads `billing.entitlement` — the single source of truth maintained by
   * Stripe webhooks + the daily reconciler. The flag is 'paid' iff the
   * workspace has a card on file OR an active/trialing subscription.
   *
   * Backwards-compatibility fallback: workspaces created before the mirror
   * existed have `entitlement === undefined`. Until the backfill runs we
   * fall back to (`hasPaymentMethod === true` ∨ `subscription.status` is
   * active/trialing) so legacy users aren't downgraded during rollout.
   *
   * @returns Hot observable that re-evaluates whenever the workspace store changes.
   */
  canUseAIImport(): Observable<boolean> {
    return toObservable(this.workspaceStore.currentWorkspace, { injector: this.injector }).pipe(
      map(workspace => {
        if (!workspace) return false;
        // Complimentary/beta grant unlocks paid features without any Stripe state.
        if (isWorkspaceCompActive(workspace.comp)) return true;
        return this.isPaidWorkspace(
          workspace.billing as WorkspaceBillingView | undefined,
          workspace.subscription as WorkspaceSubscriptionStatusView | undefined
        );
      })
    );
  }

  /**
   * Monthly AI import allowance: 3 for paid workspaces, 0 for free.
   * Mirrors `canUseAIImport` so the limit follows the same gate.
   */
  getAIImportLimit(): Observable<number> {
    return this.canUseAIImport().pipe(map(canUse => canUse ? 3 : 0));
  }

  /**
   * Pure entitlement check, exported for testability. Centralizes the
   * fallback logic so canUseAIImport / getAIImportLimit / future gates
   * can never drift apart.
   */
  isPaidWorkspace(
    billing: WorkspaceBillingView | undefined,
    subscription: WorkspaceSubscriptionStatusView | undefined
  ): boolean {
    if (billing?.entitlement === 'paid') return true;
    if (billing?.entitlement === 'free') return false;
    // Pre-mirror fallback (mirrors backend isPaidWorkspace).
    const hasCard = billing?.hasPaymentMethod === true;
    const status = subscription?.status;
    const hasActiveSubscription = status === 'active' || status === 'trialing';
    return hasCard || hasActiveSubscription || this.isRunningTrial(subscription);
  }

  /**
   * Proporio's own free trial is written `trial` (Stripe's word is `trialing`).
   * Entitling until `trialEndsAt`; a trial with no end date entitles, as on the
   * backend (`shared/subscriptionEntitlement.ts`).
   */
  private isRunningTrial(subscription: WorkspaceSubscriptionStatusView | undefined): boolean {
    if (subscription?.status !== 'trial') return false;
    const ends = subscription.trialEndsAt;
    let endsMs: number | null = null;
    if (typeof ends === 'number') endsMs = ends;
    else if (ends instanceof Date) endsMs = ends.getTime();
    else if (ends && typeof ends.toMillis === 'function') endsMs = ends.toMillis();
    else if (ends && typeof ends.seconds === 'number') endsMs = ends.seconds * 1000;
    return endsMs === null || endsMs > Date.now();
  }
}
